import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, onUnauthorized } from '../api/client'
import type { Me, Role } from '../lib/types'

interface AuthState {
  me: Me | null
  loading: boolean
  /** True when the user holds the role. The backend expands ADMIN > RESEARCHER > VIEWER. */
  can: (role: Role) => boolean
  signIn: () => void
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // /api/auth/me is permitAll and returns {authenticated:false} when anonymous, so a cold
    // load is never an error path — we just render the sign-in screen instead of the shell.
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    // A 401 from any data endpoint means the session died server-side (timeout, restart).
    // Flip back to unauthenticated so the sign-in gate replaces the app instead of every
    // view surfacing its own opaque failure.
    onUnauthorized(() => setMe((current) => ({ authenticated: false, loginUrl: current?.loginUrl })))
  }, [])

  const can = useCallback((role: Role) => !!me?.user?.roles.includes(role), [me])

  const signIn = useCallback(() => {
    // A full navigation, not fetch: the browser must follow the redirect to Microsoft.
    window.location.href = me?.loginUrl ?? '/oauth2/authorization/entra'
  }, [me])

  const signOut = useCallback(() => {
    // POST (not GET) so Spring Security's CSRF-protected logout accepts it. A hidden form
    // gives us a real navigation, which we need to follow the redirect to Microsoft's
    // end-session endpoint and clear the session there too.
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/api/auth/logout'

    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
    if (match) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = '_csrf'
      input.value = decodeURIComponent(match[1])
      form.appendChild(input)
    }

    document.body.appendChild(form)
    form.submit()
  }, [])

  return <AuthContext.Provider value={{ me, loading, can, signIn, signOut }}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
